import {
  WorkplaceRequestPublicDashboardOptions,
  WorkplaceRequestPublicDashboardTicketsParams,
  WorkplaceRequestPublicDashboardTicketsResponse
} from "../types/workplace-request-dashboards";
import { BaseSettingsEndpoints } from "./base";

const WORKPLACE_REQUEST_DASHBOARD_PUBLIC_BASE_PATH =
  "/v1/tickets/public/dashboards";
const WORKPLACE_REQUEST_DASHBOARD_MEMBER_BASE_PATH = "/v1/tickets/dashboards";

/**
 * Anonymous access to a public dashboard. Every public call authenticates with the
 * dashboard token set via `api.setTicketShareCode(...)`, sent as the
 * `X-Tactic-Ticket-Share-Code` header — no Tactic account required. The token
 * resolves the organization on its own, so no organization slug is needed and
 * these methods take no link id.
 *
 * The same header carries per-request share tokens; a public dashboard token is
 * the one prefixed `tpd_`.
 *
 * The `getMemberDashboard*` calls serve members-only links instead: they use
 * the signed-in session (not the share-code header) and take the link id.
 */
export class WorkplaceRequestDashboardPublicClient extends BaseSettingsEndpoints {
  /**
   * The dashboard's name and the filter values it exposes — its request types
   * and their fields, its offices (with time zones) and its statuses. This is
   * the link's whole scope: nothing outside it can be reached.
   */
  async getPublicDashboardOptions() {
    return this.get<WorkplaceRequestPublicDashboardOptions>(
      `${WORKPLACE_REQUEST_DASHBOARD_PUBLIC_BASE_PATH}/options`
    );
  }

  /**
   * List the requests the dashboard exposes. Filters may only narrow the link's
   * scope — values outside it are ignored rather than rejected.
   */
  async getPublicDashboardTickets(
    params: WorkplaceRequestPublicDashboardTicketsParams = {}
  ) {
    return this.get<WorkplaceRequestPublicDashboardTicketsResponse>(
      `${WORKPLACE_REQUEST_DASHBOARD_PUBLIC_BASE_PATH}/tickets${this.formatTicketsQuery(params)}`
    );
  }

  /**
   * Same as {@link getPublicDashboardOptions}, for a members-only link: opened
   * by link id with the signed-in session, not the share-code header.
   */
  async getMemberDashboardOptions(linkId: string) {
    return this.get<WorkplaceRequestPublicDashboardOptions>(
      `${WORKPLACE_REQUEST_DASHBOARD_MEMBER_BASE_PATH}/${encodeURIComponent(linkId)}/options`
    );
  }

  /** Same as {@link getPublicDashboardTickets}, for a members-only link. */
  async getMemberDashboardTickets(
    linkId: string,
    params: WorkplaceRequestPublicDashboardTicketsParams = {}
  ) {
    return this.get<WorkplaceRequestPublicDashboardTicketsResponse>(
      `${WORKPLACE_REQUEST_DASHBOARD_MEMBER_BASE_PATH}/${encodeURIComponent(linkId)}/tickets${this.formatTicketsQuery(params)}`
    );
  }

  private formatTicketsQuery(
    params: WorkplaceRequestPublicDashboardTicketsParams
  ): string {
    const query = this.formatGenericParams({
      type_id: params.type_id ?? [],
      office_id: params.office_id ?? [],
      status: params.status ?? [],
      query: params.query ?? null,
      date_field_id: params.date_field_id ?? null,
      date_from: params.date_from ?? null,
      date_to: params.date_to ?? null,
      sort_by: params.sort_by ?? null,
      sort_order: params.sort_order ?? null,
      cursor: params.cursor ?? null,
      limit: params.limit ?? null
    }).toString();

    return query ? `?${query}` : "";
  }
}
